import React, { useEffect, useState } from "react";
import { PHOTO_ROWS, photoRowOf, productLabel, productUnit, productIcon } from "./productLabel";
import { primaryButton } from "../template/templateLabels";

// The price of each product, changed one row at a time
const inputClass =
	"block w-full h-10 pl-3 pr-8 text-sm text-right text-gray-700 tabular-nums bg-white border border-gray-300 rounded-lg dark:text-gray-300 dark:bg-gray-700 dark:border-gray-600 focus:border-purple-400 focus:ring focus:ring-purple-200 focus:ring-opacity-50 focus:outline-none dark:focus:ring-gray-500";

const money = (value) => Number(value).toLocaleString() + "₮";

// The price as the field holds it: empty for a row with no price yet
const fieldOf = (price) => (price == null ? "" : String(price));

const PriceRow = ({ item, changePrice }) => {
	const [value, setValue] = useState(fieldOf(item.price));
	const [saving, setSaving] = useState(false);

	// The saved price, once it changes
	useEffect(() => {
		setValue(fieldOf(item.price));
	}, [item.price]);

	const price = Number(value);
	const valid = value.trim() !== "" && Number.isInteger(price) && price >= 0;
	const changed = valid && price !== item.price;
	// Photos and ID photos are each on sale only while their price is above 0 (the
	// cloud's routes/api/photos.js)
	const photoRow = photoRowOf(item);
	const photosOff = Boolean(photoRow) && !(item.price > 0);

	const save = async (e) => {
		e.preventDefault();
		if (!changed || saving) return;
		if (photosOff && price > 0 && !window.confirm(photoRow.confirm)) return;
		setSaving(true);
		await changePrice(item, price);
		setSaving(false);
	};

	return (
		<form onSubmit={save} className="flex flex-wrap items-center gap-4 px-5 py-4 sm:flex-nowrap">
			<span
				className="flex items-center justify-center flex-shrink-0 w-10 h-10 text-purple-600 bg-purple-100 rounded-lg dark:text-purple-200 dark:bg-purple-900"
				aria-hidden="true"
			>
				<i className={"fas " + productIcon(item)} />
			</span>
			<div className="flex-1 min-w-0">
				<p className="font-semibold text-gray-700 dark:text-gray-200">{productLabel(item)}</p>
				<p className="text-xs text-gray-500 dark:text-gray-400">
					{productUnit(item)} ·{" "}
					{item.price == null ? "үнэ тогтоогоогүй" : "одоо " + money(item.price)}
					{photosOff && " · " + photoRow.off}
				</p>
			</div>
			<div className="flex items-center w-full gap-2 sm:w-auto">
				<label className="relative flex-1 sm:w-32 sm:flex-none">
					<span className="sr-only">{productLabel(item)} үнэ</span>
					<input
						type="number"
						min="0"
						step="1"
						inputMode="numeric"
						value={value}
						onChange={(e) => setValue(e.target.value)}
						className={
							inputClass +
							(value.trim() !== "" && !valid ? " border-red-400 dark:border-red-500" : "")
						}
					/>
					<span className="absolute inset-y-0 flex items-center text-sm text-gray-400 pointer-events-none right-3">
						₮
					</span>
				</label>
				<button
					type="submit"
					disabled={!changed || saving}
					className={
						primaryButton +
						" whitespace-nowrap" +
						(!changed || saving ? " opacity-50 cursor-not-allowed" : "")
					}
				>
					{saving ? "..." : "Хадгалах"}
				</button>
			</div>
		</form>
	);
};

const Prices = ({ product, changePrice }) => {
	// The photo rows are there to be priced before the collection has them. Not when
	// the prices could not be loaded: they would look like they have no price. A row's
	// key stays the same when its first price gives it an _id, so it is not made anew.
	const rows = !product
		? []
		: [
				...product,
				...PHOTO_ROWS.filter((row) => !product.some((item) => photoRowOf(item) === row)).map(
					({ product: name, type }) => ({ product: name, type, price: null }),
				),
			];

	return rows.length > 0 ? (
		<div className="bg-white divide-y divide-gray-100 rounded-lg shadow-sm dark:bg-gray-800 dark:divide-gray-700">
			{rows.map((item) => (
				<PriceRow
					key={photoRowOf(item) ? "photo-" + item.type : item._id}
					item={item}
					changePrice={changePrice}
				/>
			))}
		</div>
	) : (
		<p className="px-5 py-4 text-sm text-gray-500 bg-white rounded-lg shadow-sm dark:text-gray-400 dark:bg-gray-800">
			Үнэ олдсонгүй
		</p>
	);
};

export default Prices;
